import {
  HydratedOpportunity,
  OpportunityBoardFilters,
} from "@forgepoint/shared";
import { matchesCustomFieldFilters } from "@/lib/customFieldFilters";
import type { OpportunityArchiveMode } from "./opportunityFilterTypes";

/**
 * Whether an opportunity matches the active sales-rep, estimator, company,
 * location, custom field, and created date filters. Task-assignee filtering can't be
 * evaluated client-side (assignees live on associated tasks), so callers rely
 * on the server applying that filter.
 * @param opp - The opportunity to test
 * @param filters - The active board filters
 * @returns True when the opportunity satisfies the client-evaluable filters
 */
export function matchesBoardFilters(
  opp: HydratedOpportunity,
  filters: OpportunityBoardFilters,
): boolean {
  if (
    filters.salesRepUserIds?.length &&
    (!opp.salesRepUserId ||
      !filters.salesRepUserIds.includes(opp.salesRepUserId))
  ) {
    return false;
  }
  if (
    filters.estimatorUserIds?.length &&
    !(opp.estimatorUserIds ?? []).some((id) =>
      filters.estimatorUserIds?.includes(id),
    )
  ) {
    return false;
  }
  if (
    filters.companyIds?.length &&
    (!opp.companyId || !filters.companyIds.includes(opp.companyId))
  ) {
    return false;
  }
  if (
    filters.locationIds?.length &&
    (!opp.locationId || !filters.locationIds.includes(opp.locationId))
  ) {
    return false;
  }
  if (!matchesCustomFieldFilters(opp.customFields, filters.customFields)) {
    return false;
  }
  if (filters.createdAfter && opp.createdAt < filters.createdAfter) {
    return false;
  }
  if (filters.createdBefore && opp.createdAt > filters.createdBefore) {
    return false;
  }
  return true;
}

/**
 * Whether an opportunity's archived state is visible in the given archive view.
 * @param opp - The opportunity to test
 * @param archiveMode - The board's archive view
 * @returns True when the opportunity is visible in that view
 */
export function matchesArchiveMode(
  opp: HydratedOpportunity,
  archiveMode: OpportunityArchiveMode,
): boolean {
  if (archiveMode === "active") {
    return opp.archivedAt === null;
  }
  if (archiveMode === "archived") {
    return opp.archivedAt !== null;
  }
  return true;
}
